import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { SportIds } from 'constants/app';
import { BetSidesTypes, BettingTypes } from 'constants/markets';
import { BetSides } from 'constants/myBets';
import { useMarketUnits } from 'hooks/useMarketUnits';
import { getEnableFancyMarketsViewOnCricket, getEnableLineMarketsBranding } from 'redux/modules/appConfigs/selectors';
import { getIsFancyViewEnabled } from 'redux/modules/competitions/selectors';
import { TLabels } from 'types/betslip';
import { getBetSideLabelType } from 'utils/market';

import useOddsType from './useOddsType';

export const useBetslipLabels = ({
  eventTypeId,
  bettingType,
  marketUnit,
  isInlinePlacement = false,
  fancyView
}: {
  eventTypeId: string;
  bettingType?: string;
  marketType?: string;
  marketUnit?: string | null;
  isInlinePlacement?: boolean;
  fancyView?: boolean;
}) => {
  const { t } = useTranslation();

  const enableFancyMarketsViewOnCricket = useSelector(getEnableFancyMarketsViewOnCricket);
  const enableLineMarketsBranding = useSelector(getEnableLineMarketsBranding);
  const isFancyView = useSelector(getIsFancyViewEnabled);

  const { isAmericanOdds } = useOddsType();
  const marketUnitTranslated = useMarketUnits(marketUnit || 'points');

  const isLineMarket = bettingType === BettingTypes.LINE;
  const isCricketMarket = eventTypeId === SportIds.CRICKET;

  const labels: TLabels = {};

  [BetSides.Back, BetSides.Lay].forEach(type => {
    const betType = type.toLowerCase();
    const isBack = type === BetSides.Back;
    const stakeTranslationKey = isBack
      ? `betslip.labels.${isAmericanOdds ? 'toBet' : 'stake'}`
      : `betslip.labels.${isAmericanOdds ? 'toWin' : 'size.lay'}`;
    const profitInlinePlacementTranslationKey = isBack ? 'other.labels.profit' : 'other.labels.liability';
    const profitUSInlinePlacementTranslationKey = isBack ? 'betslip.labels.toWin' : 'betslip.labels.toBet';
    const profitBetslipTranslationKey = isBack
      ? `betslip.labels.${isAmericanOdds ? 'toWin' : 'profit'}`
      : `betslip.labels.${isAmericanOdds ? 'toBet' : 'liability'}`;
    const profitTranslationKey = isInlinePlacement
      ? isAmericanOdds
        ? profitUSInlinePlacementTranslationKey
        : profitInlinePlacementTranslationKey
      : profitBetslipTranslationKey;

    labels[type] = {
      bet: t(
        'betslip.labels.type.' +
          getBetSideLabelType({
            betSide: betType,
            isLineMarket,
            isCricketMarket,
            enableFancyMarketsViewOnCricket,
            enableLineMarketsBranding,
            isFancyView: isFancyView || !!fancyView
          })
      ),
      odds: isLineMarket
        ? /** Use betslip.labels.units.back for both types if market is LINE */
          t('betslip.labels.units.back', { UNITS: marketUnitTranslated })
        : t(`betslip.labels.price.${betType}`),
      stake: isInlinePlacement
        ? t(!isAmericanOdds ? 'other.labels.stake' : isBack ? 'betslip.labels.toBet' : 'betslip.labels.toWin')
        : t(stakeTranslationKey),
      profit: t(profitTranslationKey)
    };
  });

  return labels;
};

export const useBetSideLabel = ({
  betSide,
  isLineMarket,
  isCricketMarket,
  defaultLabel = 'betslip.labels.type.',
  fancyView = false
}: {
  betSide: string;
  isLineMarket: boolean;
  isCricketMarket: boolean;
  defaultLabel?: string;
  fancyView?: boolean;
}) => {
  const { t } = useTranslation();

  const enableLineMarketsBranding = useSelector(getEnableLineMarketsBranding);
  const enableFancyMarketsViewOnCricket = useSelector(getEnableFancyMarketsViewOnCricket);
  const isFancyView = useSelector(getIsFancyViewEnabled); // this works only if user navigated to fancy market (through navigation)

  const isBack = betSide.toLowerCase() === BetSidesTypes.back;

  if (enableFancyMarketsViewOnCricket && isCricketMarket && isLineMarket && (isFancyView || fancyView)) {
    return isBack ? t('betslip.labels.type.no') : t('betslip.labels.type.yes');
  }

  if (isLineMarket && enableLineMarketsBranding) {
    return isBack ? t('betslip.labels.type.under') : t('betslip.labels.type.over');
  }

  return t(`${defaultLabel}${betSide.toLowerCase()}`);
};
